<template>
  <div
    ref="cardElement"
    class="model-container"
    :class="{ 'position-relative': !query.isInContext }"
    :style="{
      minHeight: getCardHeight( event, query, textareaHeight ?? 0),
      marginTop: system.queries[event.index].length === 1 ? (!showEntities ? '30px' : '52px') : '0'
    }"
  >
    <div v-if="!query.isInContext" class="read-model-context w-100 text-center text-no-wrap text-white">
      Integration with other bounded contexts
    </div>
    <div
      :id="`query-${system.index}-${event.index}-${queryIndex}`"
      class="requirement-card hover-container height-transition-hover-container"
      :style="{
        'border-color': query.isInContext ? cardTypeColor(queryCardType) : '#868686',
        'border-top-width': query.isInContext ? undefined : '18px',
        '--transitionContainerHeight': query.dataModel.originalIndex === -1 ? '24px' : '16px'
      }"
    >
      <beam-card
        v-if="query.dataModel.originalIndex === -1"
        :id="`query-${system.index}-${event.index}-${queryIndex}`"
        :event="event"
        :card="query"
      />
      <div v-else>
        <domain-model-query-inputs
          :event="event"
          :query="query"
          :queryIndex="queryIndex"
          :descriptions="descriptions"
          @updateTextareaHeight="updateTextareaHeight"
          @updateDescription="updateDescription"
          @getTextareaRef="getTextAreaRef"
        />
        <div
          v-if="showEntities && !!(query.dataModel?.dataFields ?? [])?.length"
          class="py-2 fields"
          ref="fieldsRef"
          :data-event-id="event.id"
          :data-model-index="query.dataModel.originalIndex"
        >
          <template v-for="(field, index4) in (query.dataModel?.dataFields ?? [])" :key="field.name">
            <div class="sortableRow">
              <div class="field-name d-flex align-center">
                <v-btn
                  class="border-none cursor-handle sortHandle"
                  size="small"
                  variant="plain"
                  tabindex="-1"
                  :icon="field.type === 'filter' ? 'mdi-filter-variant' : 'mds:drag-indicator'"
                  :disabled='store.state.isWorkflowDisabled'
                />
                <quick-rename-input
                  :id="`read-model-${event.id}-field-${index4}`"
                  :value="field.name"
                  @onChange="(e) => blurDataField(e, event, query.dataModel.originalIndex, index4)"
                  :density="`compact`"
                  :max-length="30"
                  style="flex-grow: 1;"
                  :disabled="store.state.isWorkflowDisabled"
                />
                <v-chip
                  v-if="field.relatedEntityId && getRelatedEntity(field)"
                  density="compact"
                  size="small"
                  :closable="!store.state.isWorkflowDisabled"
                  class="related-entity-chip px-2 ml-1"
                >
                  {{ field.cardinality === 'one-to-many'
                    ? `[ ${getRelatedEntity(field)?.name} ]`
                    : `${getRelatedEntity(field)?.name}` }}
                  <template v-if="!store.state.isWorkflowDisabled" #close>
                    <v-icon
                      icon="mdi-close-circle"
                      color="#888"
                      :size="16"
                      class="remove-entity-icon"
                      @click.stop="onSelectRelatedEntity(event, query.id, index4, null)"
                    />
                  </template>
                </v-chip>
                <v-tooltip v-if="isNameNotInEntity(query.entityId, field.name)" location="right" max-width="300px">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" style="cursor: default;">⚠️</span>
                  </template>
                  <span>This field on the read model has no corresponding entity field.</span>
                </v-tooltip>
                <v-tooltip
                  v-else-if="isSelectedEntityMismatch(query, field)"
                  location="bottom"
                  max-width="300px"
                  open-delay="100"
                >
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" size="14px" icon="mdi-alert" color="#ED1C0C" />
                  </template>
                  <span>Selected entity does not match with the related entity of the corresponding entity field.</span>
                </v-tooltip>
                <!-- Three Dot Menu -->
                <v-menu v-if="!store.state.isWorkflowDisabled" class="card-menu">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon="mdi-dots-vertical"
                      class="read-model-field-actions-btn"
                      size="small"
                      variant="plain"
                      v-bind="props"
                    />
                  </template>
                  <v-list density="compact">
                    <v-list-item
                      @click="toggleFieldFilter(event, query.id, index4)"
                      :disabled="field.relatedEntityId"
                    >
                      <v-list-item-title>
                        <v-icon class="mr-2" color="grey" icon="mdi-filter-variant" />
                        {{ field.type === 'filter' ? 'Remove Filter' : 'Add Filter' }}
                      </v-list-item-title>
                    </v-list-item>
                    <select-entity
                      :eventId="event.id"
                      @onSelectEntity="(entity) => onSelectRelatedEntity(event, query.id, index4, entity)"
                    >
                      <v-list-item>
                        <v-list-item-title style="cursor: pointer;">
                          <v-icon class="mr-2" color="grey">mdi-database</v-icon> Select Entity
                        </v-list-item-title>
                      </v-list-item>
                    </select-entity>
                    <v-list-item @click="removeDataField(event, query.id, index4)">
                      <v-list-item-title>
                        <v-icon class="mr-2" color="grey" icon="mdi-trash-can-outline" /> Delete
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <div
                v-if="field.relatedEntityId"
                ref="subFieldsRef"
                :data-event-id="event.id"
                :data-model-index="query.dataModel.originalIndex"
                :data-field-index="index4"
              >
                <div
                  v-for="(item, index5) in field.dataFields"
                  :key="item.name"
                  class="pl-4 field-name d-flex align-center sortableRow"
                >
                  <v-btn
                    class="border-none cursor-handle sortHandle"
                    size="small"
                    variant="plain"
                    tabindex="-1"
                    :icon="item.type === 'filter' ? 'mdi-filter-variant' : 'mds:drag-indicator'"
                    :disabled='store.state.isWorkflowDisabled'
                  />
                  <quick-rename-input
                    :id="`read-model-${event.id}-field-${index4}-entity-${index5}`"
                    :value="item.name"
                    :density="`compact`"
                    @onChange="(e) => blurSubDataField(e, event, query.id, index4, index5)"
                    :max-length="30"
                    style="flex-grow: 1;"
                    :disabled='store.state.isWorkflowDisabled'
                  />
                  <!-- Warning icon with tooltip -->
                  <v-tooltip v-if="isNameNotInEntity(field.relatedEntityId, item.name)" location="right">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" style="cursor: default;">⚠️</span>
                    </template>
                    <span>This field on the read model has no corresponding <br /> entity field.</span>
                  </v-tooltip>
                  <v-menu v-if="!store.state.isWorkflowDisabled" class="card-menu">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        icon="mdi-dots-vertical"
                        class="read-model-field-actions-btn"
                        size="small"
                        variant="plain"
                        v-bind="props"
                      />
                    </template>
                    <v-list density="compact">
                      <v-list-item @click="toggleFieldFilter(event, query.id, index4, index5)">
                        <v-list-item-title>
                          <v-icon class="mr-2" color="grey" icon="mdi-filter-variant" />
                          {{ item.type === 'filter' ? 'Remove Filter' : 'Add Filter' }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="removeSubDataField(event, query.id, index4, index5)">
                        <v-list-item-title>
                          <v-icon class="mr-2" color="grey" icon="mdi-trash-can-outline" /> Delete
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
                <div v-if="!store.state.isWorkflowDisabled" class="d-flex align-center pl-4 btn-add-sub-field">
                  <v-tooltip location="bottom" open-delay="100">
                    <template v-slot:activator="{ on, props }">
                      <v-btn
                        v-bind="props"
                        v-on="{ ...on }"
                        size="small"
                        variant="plain"
                        @click="addSubField(event, query.id, index4)"
                        tabindex="-1"
                        icon="mdi-plus"
                      />
                    </template>
                    <span>Add field</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </template>
        </div>
        <toggle
          mandatory
          class="py-1"
          :value="query.cardinality"
          :options="options"
          :disabled="store.state.isWorkflowDisabled"
          @update:modelValue="(val) => updateCardinality(query.id, val)"
        />
      </div>
      <domain-model-card-toolbar
        v-if="!store.state.isWorkflowDisabled"
        :showAi="query.dataModel.originalIndex === -1"
        :showFilters="true"
        :event="event"
        :card="query"
        :menuPositioningActivator="cardElement"
      />
    </div>
    <div
      v-if="!store.state.isWorkflowDisabled && queryIndex === system.queries[event.index].length - 1"
      class="add-more-query-btn"
    >
      <v-tooltip location="bottom" open-delay="100">
        <template v-slot:activator="{ on, props }">
          <v-btn
            v-bind="props"
            v-on="{ ...on }"
            size="small"
            tabindex="-1"
            icon="mdi-plus"
            @click="createCard(event.id, queryCardType.id, `<p>${queryCardType.title} for ${event.description}</p>`)"
          />
        </template>
        <span>Add {{ queryCardType?.title }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import BeamCard from '@/components/BeamCard'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import QuickRenameInput from '@/components/QuickRenameInput'
import DomainModelCardToolbar from '@/components/DomainModelCardToolbar'
import DomainModelQueryInputs from '@/components/DomainModelQueryInputs.vue'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import useDomainModel from '@/composables/useDomainModel'
import { useSortFields } from '@/composables/useSortFields'
import workflowApi from '@/api/workflowApi'

const {
  query,
  queryIndex,
  system,
  event,
  showEntities,
  descriptions
} = defineProps({
  query: Object,
  queryIndex: Number,
  system: Object,
  event: Object,
  showEntities: Boolean,
  descriptions: Array
})

const store = useStore()
const route = useRoute()
const workflow = computed(() => store.state.workflow.workflow)
const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(requirementTypes.value))

const {
  cardTypeColor,
  getRelatedEntity,
  isNameNotInEntity,
  addSubField,
  blurDataField,
  blurSubDataField,
  removeSubDataField,
  onSelectRelatedEntity,
  removeDataField,
  getMatchingFieldInEntity,
  toggleFieldFilter,
  getCardHeight,
  createCard
} = useDomainModel()
const { fieldsRef, subFieldsRef, initSortable, destroySortable } = useSortFields()

const options = [
  { value: 'one-to-many', label: 'List' },
  { value: 'one-to-one', label: 'Single Item' }
]

const cardElement = ref(null)
const textareaRef = ref(null)
const textareaHeight = ref(0)

const getTextAreaRef = (element) => {
  textareaRef.value = element?.$el?.querySelector('textarea')
  updateTextareaHeight()
}

const updateTextareaHeight = async () => {
  await nextTick()
  if (textareaRef.value) {
    textareaHeight.value = textareaRef.value.scrollHeight
  }
}

const getQueryRootEntity = (entityId) => {
  return (workflow.value.dataModels || [])?.find((entity) => entity.id === entityId)
}

const isSelectedEntityMismatch = (query, field) => {
  const rootEntity = getQueryRootEntity(query.entityId)

  if (!rootEntity) {
    return false
  }

  const matchingFieldInEntity = getMatchingFieldInEntity(rootEntity, field.name)

  if (!matchingFieldInEntity || field.relatedEntityId === matchingFieldInEntity?.relatedEntityId) {
    return false
  }

  // Checking for actual entity existence
  const commandFieldRelatedEntity = field.relatedEntityId
    ? workflow.value.dataModels?.find((entity) => entity.id === field.relatedEntityId)
    : true
  const entityFieldRelatedEntity = matchingFieldInEntity?.relatedEntityId
    ? workflow.value.dataModels?.find((entity) => entity.id === matchingFieldInEntity?.relatedEntityId)
    : true

  return !!commandFieldRelatedEntity && !!entityFieldRelatedEntity
}

const updateDescription = (card, description) => {
  if (card.description === description) {
    return
  }

  workflowApi.updateRequirementsJsonMutation(route.params.id, [{ requirementId: card.id, description }])
}

const updateCardinality = (cardId, cardinality) => {
  workflowApi.updateRequirementsJsonMutation(route.params.id, [{ requirementId: cardId, cardinality }])
}

watch(() => query, () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      initSortable()
    })
  })
})

onMounted(() => {
  initSortable()
})

onBeforeUnmount(() => {
  destroySortable()
})
</script>

<style scoped>
.card-menu .v-list-item:hover{
  background-color: var(--light-2) !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px 0 !important;
}

.sortableRow:hover .btn-add-sub-field {
  opacity: 1;
  height: 16px;

  .v-btn {
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}

.btn-add-sub-field {
  opacity: 0;
  height: 0;
  transition: all 0.2s;
}

:deep(.v-textarea .v-field__input) {
  font-size: 12px;
  padding: 8px;
}

.add-more-query-btn {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  transition: all 0.2s;
  opacity: 0;
}

.read-model-context {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
}
</style>
